import {
  BarChartOutlined,
  BookFilled,
  CopyOutlined,
  DeleteOutlined,
  FileDoneOutlined,
  ProjectOutlined,
  RedoOutlined,
  ScanOutlined,
  TeamOutlined,
  MenuOutlined,
} from '@ant-design/icons';
import { Avatar, Button, Dropdown, Layout, Menu, MenuProps, Tabs } from 'antd';
import { SubMenuType } from 'antd/es/menu/hooks/useItems';
import { useEffect, useState } from 'react';
import { BiBuildings } from 'react-icons/bi';
import { FiSettings } from 'react-icons/fi';
import { LiaMoneyCheckAltSolid } from 'react-icons/lia';
import { MdEmojiPeople } from 'react-icons/md';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { getDetailBusinessProfile } from 'src/api/business_profile';
import { getPackageTypes } from 'src/api/connection/index';
import { checkPaymentMethod } from 'src/api/register_and_cancel_and_load_package';
import Loading from 'src/components/CLoading';
import CModal from 'src/components/CModal';
import ModalJobProgress from 'src/components/modals/ModalJobProgress';
import { ModalChangePassword } from 'src/components/modals/ModalChangePassword';
import useWindowSize from 'src/hooks/useWindowSize';
import { setUserProfileState } from 'src/redux/slice/userProfile';
import { EXTERNAL_MENU_FLAG, EXTERNAL_MENU_KEYS, USER_MENU_KEYS } from 'src/shared/constants';
import { DataEnterprise } from 'src/shared/interface/user_profile';
import { v4 as uuidv4 } from 'uuid';
import { useAppDispatch, useAppSelector } from '../../redux/hook';
import { reset, setGlobalState } from '../../redux/slice/globalSlice';
import { clearLocalStorage } from 'src/utils';
import { getMasterData } from 'src/api';

const { Header, Sider } = Layout;
type TargetKey = React.MouseEvent | React.KeyboardEvent | string;

const hideMenus = ['changeSim/createChangeSim'];

// Type for PMS node
type PmsNode = {
  id: string;
  name: string;
  route: string;
  enable: boolean;
  parentId: string | null;
  child: PmsNode[];
};
// Recursive function to filter menu items based on enabled PMS nodes
function filterMenuByPms(menu: MenuProps['items'], pms: PmsNode[]): MenuProps['items'] {
  if (!Array.isArray(menu) || !Array.isArray(pms)) return [];

  return menu
    .map((item: any) => {
      const pmsNode = pms.find((p: PmsNode) => p.route === item.key);
      const children = item.children || [];
      let filteredChildren: any[] = [];

      if (children.length > 0) {
        // Find PMS child nodes for this node
        const pmsChild = pmsNode ? pmsNode.child || [] : [];
        const childResult = filterMenuByPms(children, pmsChild);

        filteredChildren = childResult || [];
      }

      // Node is enabled if pmsNode.enable is true or any child is enabled
      if ((pmsNode && pmsNode.enable) || filteredChildren.length > 0) {
        return {
          ...item,
          children: filteredChildren.length > 0 ? filteredChildren : undefined,
        };
      }
      return null;
    })
    .filter(Boolean);
}
const menuItems: MenuProps['items'] = [
  {
    key: `dashboard`,
    label: `Dashboard`,
    icon: <ProjectOutlined />,
  },
  {
    key: `settings`,
    label: `Thiết lập hệ thống`,
    icon: <FiSettings />,
  },
  {
    key: `permissions`,
    label: `User và phân quyền`,
    icon: <TeamOutlined />,
  },

  {
    key: 'profile',
    label: `Quản lí thông tin doanh nghiệp`,
    icon: <BiBuildings />,
  },
  {
    key: 'user-profile',
    label: `Quản lý hồ sơ người sử dụng`,
    icon: <MdEmojiPeople />,
    children: [
      {
        key: `user-profile/list`,
        label: `Danh sách hồ sơ người sử dụng`,
      },
      {
        key: `user-profile/batch-create`,
        label: `Tạo mới hồ sơ người sử dụng theo lô`,
      },
    ],
  },
  {
    key: `localpay`,
    label: `Quản lý Localpay`,
  },
  {
    key: `account-data`,
    label: `Quản lý tài khoản data`,
  },
  {
    key: `changeSim`,
    label: `Đơn hàng Đổi sim`,
    icon: <RedoOutlined />,
  },
  {
    key: `changeSim/createChangeSim`,
    label: `Tạo mới đơn hàng đổi sim`,
    icon: <RedoOutlined />,
  },
  {
    key: 'connection',
    label: `Đấu nối`,
    children: [
      {
        key: `connection/single`,
        label: `Đấu nối đơn lẻ`,
      },
      {
        key: `connection/batch`,
        label: `Đấu nối theo lô`,
      },

      {
        key: `connection/history`,
        label: `Lịch sử đấu nối`,
      },
      {
        key: EXTERNAL_MENU_KEYS.UPDATE_SUBS_REQ,
        label: 'Yêu cầu CCQ',
      },
    ],
  },
  {
    key: `order-assigned-package`,
    label: `Gán gói`,
    icon: <FileDoneOutlined />,
    children: [
      {
        key: `order-assigned-package/list`,
        label: `Đơn hàng gán gói`,
      },
      {
        key: `order-assigned-package/history`,
        label: `Lịch sử gán gói`,
      },
    ],
  },

  {
    key: `active-sim`,
    label: `Quản lý danh sách TB`,
    icon: <FileDoneOutlined />,
    children: [
      {
        key: `active-sim/list`,
        label: `Danh sách TB`,
      },
      {
        key: `active-sim/search`,
        label: `Tra cứu thuê bao`,
      },
      {
        key: `active-sim/sim900/list`,
        label: `TB chờ kích hoạt`,
      },
    ],
  },
  {
    key: `esim-management`,
    label: `Quản lý eSIM`,
    icon: <ScanOutlined />,
  },
  {
    key: `register-and-cancel-and-load-package`,
    label: `Nạp/Đăng ký/Huỷ gói`,
    icon: <CopyOutlined />,
    children: [
      {
        key: `register-and-cancel-and-load-package/register/list`,
        label: `Đơn hàng Đăng ký gói`,
      },
      {
        key: `register-and-cancel-and-load-package/data-load/list`,
        label: `Nạp data`,
      },
      {
        key: `register-and-cancel-and-load-package/cancel/list`,
        label: `Đơn hàng Huỷ gói`,
      },
      {
        key: `register-and-cancel-and-load-package/register/history`,
        label: `Lịch sử đăng ký dịch vụ`,
      },
    ],
  },
  {
    key: `topup`,
    label: `Nạp tiền`,
    icon: <LiaMoneyCheckAltSolid />,
    children: [
      {
        key: `topup/list`,
        label: `Đơn hàng nạp tiền`,
      },
      {
        key: `topup/history`,
        label: `Lịch sử nạp tiền`,
      },
    ],
  },
  {
    key: `report`,
    label: `Báo cáo`,
    icon: <BarChartOutlined />,
    children: [
      {
        key: `report/data-usage`,
        label: `Lưu lượng data của thuê bao`,
      },
    ],
  },
  {
    key: `transaction`,
    label: `Danh sách Giao dịch`,
    icon: <BookFilled />,
  },
];
const ManagementPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { openTabs, activeKeyMenu } = useAppSelector((state) => state.global);
  const [activeKey, setActiveKey] = useState(location.pathname.split('/')[2]);
  const [items, setItems] = useState(openTabs);
  const [paymentMethod, setPaymentMethod] = useState<string[]>([]);
  const [menus, setMenus] = useState(menuItems);
  const [isChangePasswordModalVisible, setIsChangePasswordModalVisible] = useState(false);
  const { masterData } = useAppSelector((state) => state.global);

  const pms = useAppSelector((state) => state.global.masterData?.pms || []);
  const [collapsed, setCollapsed] = useState(false);
  const size = useWindowSize();

  const add = (newTabKey: string, activeKeyId = '') => {
    const newActiveKey = newTabKey?.split('/')[0];
    const _itemFound = items?.find((x) => x.key === newTabKey);
    let _menuItem = menuItems?.find((x) => x?.key === newActiveKey) as SubMenuType;

    if (_menuItem?.children) {
      _menuItem = _menuItem.children?.find((item) => item?.key === newTabKey) as SubMenuType;
    }
    if (!_itemFound && _menuItem) {
      setItems([
        ...items,
        {
          label: _menuItem.label?.toString() ?? '',
          children: '',
          key: newTabKey,
          closable: newTabKey !== 'dashboard',
        },
      ]);
      dispatch(
        setGlobalState({
          openTabs: [
            ...items,
            {
              label: _menuItem.label?.toString() ?? '',
              children: '',
              key: newTabKey,
              closable: newTabKey !== 'dashboard',
            },
          ],
        }),
      );
    }
    dispatch(
      setGlobalState({
        activeKeyMenu: newTabKey,
        activeKeyId: activeKeyId,
      }),
    );
    setActiveKey(newTabKey);
    navigate(`${newTabKey}`);
  };

  const remove = (targetKey: TargetKey) => {
    const targetIndex = items.findIndex((pane) => pane.key === targetKey);
    const newPanes = items.filter((pane) => pane.key !== targetKey);
    if (newPanes?.length && targetKey === activeKey) {
      const { key } = newPanes[targetIndex === newPanes?.length ? targetIndex - 1 : targetIndex];
      dispatch(
        setGlobalState({
          activeKeyMenu: key,
        }),
      );
      setActiveKey(key);
      navigate(`${key}`);
    }
    setItems(newPanes);
    dispatch(
      setGlobalState({
        openTabs: newPanes,
      }),
    );
  };

  const onEdit = (targetKey: TargetKey) => {
    remove(targetKey);
  };

  const onChange = (newActiveKey: string) => {
    dispatch(
      setGlobalState({
        activeKeyMenu: newActiveKey,
      }),
    );
    setActiveKey(newActiveKey);
    navigate(`${newActiveKey}`);
  };
  const handleLogout = () => {
    // localStorage.removeItem('token');
    dispatch(reset());
    clearLocalStorage();
    navigate('/login');
  };

  const handleOpenChangePasswordModal = () => {
    setIsChangePasswordModalVisible(true);
  };

  const handleCloseChangePasswordModal = () => {
    setIsChangePasswordModalVisible(false);
  };

  const handleMenuClick = ({ key }: { key: string }) => {
    const menuActions: Record<string, () => void> = {
      [USER_MENU_KEYS.CHANGE_PASSWORD]: handleOpenChangePasswordModal,
      [USER_MENU_KEYS.LOGOUT]: handleLogout,
    };

    const action = menuActions[key];
    if (action) {
      action();
    }
  };

  const userMenuItems: MenuProps['items'] = [
    {
      key: USER_MENU_KEYS.CHANGE_PASSWORD,
      className: 'rounded-t-xl',
      label: 'Đổi mật khẩu',
    },
    {
      key: USER_MENU_KEYS.LOGOUT,
      className: 'rounded-b-xl',
      label: 'Đăng xuất',
    },
  ];

  const menu = <Menu className='rounded-xl' onClick={handleMenuClick} items={userMenuItems} />;

  useEffect(() => {
    if (activeKeyMenu) {
      setCollapsed(true);
    }
    // eslint-disable-next-line no-debugger
    setItems(openTabs);
    if (activeKeyMenu) {
      setActiveKey(activeKeyMenu);
      navigate('/management/' + activeKeyMenu);
    }
    if (
      (paymentMethod.length > 0 &&
        !paymentMethod.includes('DataAccount') &&
        !hideMenus.includes('account-data')) ||
      paymentMethod.length === 0
    ) {
      const hidden = [...hideMenus, 'account-data'];
      setMenus(menuItems.filter((menu: any) => !hidden.includes(menu?.key)));
    }
    if (paymentMethod.length > 0 && paymentMethod.includes('DataAccount')) {
      setMenus(menuItems.filter((menu: any) => !hideMenus.includes(menu?.key)));
    }
  }, [openTabs, activeKeyMenu, paymentMethod]);

  useEffect(() => {
    add(activeKeyMenu);
    fetchDataEnterprise();
    fetchPackageType();
    fetchPaymentMethod();
    fetchActiveTermsConfig();
  }, []);

  const fetchDataEnterprise = async () => {
    try {
      const res: DataEnterprise | undefined = await getDetailBusinessProfile();
      if (res) {
        dispatch(setUserProfileState({ dataEnterprise: res }));
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchPackageType = async () => {
    const listPackageTypeRes = await getPackageTypes();
    if (listPackageTypeRes) {
      dispatch(
        setGlobalState({
          listPackageType: listPackageTypeRes,
        }),
      );
    }
  };
  const fetchActiveTermsConfig = async () => {
    const [resMasterDataActiveTerms, resMasterDataActiveTermsConfig] = await Promise.all([
      getMasterData({
        code: 'EnterpriseActiveTerm',
        option: 2,
      }),
      getMasterData({
        code: 'EnterpriseActiveTermConfig',
      }),
    ]);
    if (resMasterDataActiveTerms && resMasterDataActiveTermsConfig) {
      dispatch(
        setGlobalState({
          masterData: {
            ...masterData,
            activeTerms: resMasterDataActiveTerms,
            activeTermsConfig: resMasterDataActiveTermsConfig,
          },
        }),
      );
    }
  };

  const fetchPaymentMethod = async () => {
    const res = await checkPaymentMethod();
    if (res) {
      setPaymentMethod(res);
    }
  };

  const onClearTabs = () => {
    dispatch(
      setGlobalState({
        activeKeyMenu: '',
        openTabs: [],
      }),
    );
  };

  return (
    <Layout className='h-screen'>
      <Header className='flex justify-between !px-4 md:!px-[50px]'>
        <div className='flex items-center gap-4'>
          <Button
            type='text'
            icon={<MenuOutlined style={{ fontSize: '20px', color: 'white' }} />}
            onClick={() => setCollapsed(!collapsed)}
            className='xl:hidden'
          />
          <div className='logo' />
        </div>
        <div>
          <Dropdown trigger={['click']} overlay={menu}>
            <Avatar
              className='cursor-pointer'
              style={{ backgroundColor: '#e2e9f3', verticalAlign: 'middle', color: 'black' }}
              size='large'
            >
              U
            </Avatar>
          </Dropdown>
        </div>
      </Header>
      <Layout>
        <Sider collapsed={collapsed && size.width <= 768} className='md:mt-12' width={300}>
          <Menu
            selectedKeys={[activeKeyMenu]}
            defaultSelectedKeys={[activeKeyMenu]}
            mode='inline'
            style={{ height: '100%', borderRight: 0, overflowY: 'auto' }}
            items={filterMenuByPms(
              menus.filter((menu: any) => !hideMenus.includes(menu?.key)),
              pms,
            )}
            onClick={(e) => {
              if (e.key.startsWith('http')) {
                window.open(e.key + EXTERNAL_MENU_FLAG, '_blank');
              } else {
                add(e.key, uuidv4());
                navigate(`${e.key}`);
              }
            }}
          />
        </Sider>
        <Layout className='md:px-3 md:pb-6'>
          <Loading />
          <Tabs
            hideAdd
            type='editable-card'
            onChange={onChange}
            onEdit={onEdit}
            activeKey={activeKey}
            items={items}
            className={'mt-2'}
            tabBarExtraContent={
              <div className={`clear-tabs ${!openTabs?.length ? 'invisible' : ''}`}>
                <Button
                  className={`mt-1`}
                  danger
                  type={'link'}
                  size={'small'}
                  icon={<DeleteOutlined />}
                  onClick={onClearTabs}
                />
              </div>
            }
          />
          <div
            className='p-3 md:p-6 pb-8'
            style={{
              margin: 0,
              minHeight: 280,
              // height: '90%',
              background: 'white',
              overflow: 'auto',
              borderRadius: 12,
            }}
          >
            <Outlet />
          </div>
        </Layout>
      </Layout>
      <CModal />
      <ModalJobProgress />
      <ModalChangePassword
        visible={isChangePasswordModalVisible}
        onClose={handleCloseChangePasswordModal}
      />
    </Layout>
  );
};

export default ManagementPage;
